/**
 * NewGroupModal Component
 *
 * Modal to create a new group conversation: pick a title and a set of
 * participants. Creator is automatically added as admin by the service.
 *
 * @module components/messages/NewGroupModal
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { createConversation } from '../../services/chatService';
import { formatUserRole } from '../../utils/formatters';
import { toast } from '../../utils/toast';
import TaskAssigneeAvatar from '../tasks/shared/TaskAssigneeAvatar';

// Adapter: wrap createConversation with the group-specific signature
const createGroupConversation = (groupTitle, userIds) =>
  createConversation({ type: 'group', name: groupTitle, participantIds: userIds });

const fetchChatUsers = async () => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, role')
    .order('full_name', { ascending: true });
  if (error) throw error;
  return data || [];
};

const NewGroupModal = ({ show, onHide, onConversationOpen }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitError, setSubmitError] = useState(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['chat-users'],
    queryFn: fetchChatUsers,
    enabled: show,
    staleTime: 1000 * 60,
  });

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users
      .filter((u) => u.id !== user?.id)
      .filter((u) => {
        if (!term) return true;
        const name = (u.full_name || '').toLowerCase();
        const role = (u.role || '').toLowerCase();
        return name.includes(term) || role.includes(term);
      });
  }, [users, search, user]);

  const toggleUser = (userId) => {
    setSelectedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const groupMutation = useMutation({
    mutationFn: async ({ groupTitle, userIds }) => {
      const { data, error } = await createGroupConversation(groupTitle, userIds);
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Grupo creado');
      if (onConversationOpen && data?.id) onConversationOpen(data.id);
      handleClose();
    },
    onError: (err) => {
      setSubmitError(err?.message || 'No se pudo crear el grupo');
      toast.error(err?.message || 'No se pudo crear el grupo');
    },
  });

  const handleClose = () => {
    setTitle('');
    setSearch('');
    setSelectedIds([]);
    setSubmitError(null);
    onHide();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);

    const trimmed = title.trim();
    if (!trimmed) {
      setSubmitError('El nombre del grupo es requerido.');
      return;
    }
    if (selectedIds.length === 0) {
      setSubmitError('Selecciona al menos un participante.');
      return;
    }

    groupMutation.mutate({ groupTitle: trimmed, userIds: selectedIds });
  };

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedIds.includes(u.id)),
    [users, selectedIds]
  );

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-people me-2"></i>
          Nuevo grupo
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nombre del grupo</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ej. Equipo Proyecto Alpha"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              required
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>
              Participantes
              {selectedIds.length > 0 && (
                <Badge bg="primary" className="ms-2">
                  {selectedIds.length}
                </Badge>
              )}
            </Form.Label>
            <Form.Control
              type="search"
              placeholder="Buscar usuario..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Form.Group>

          {selectedUsers.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-2">
              {selectedUsers.map((u) => (
                <Badge
                  key={u.id}
                  bg="light"
                  text="dark"
                  className="d-inline-flex align-items-center gap-1 py-2 px-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleUser(u.id)}
                  title="Quitar"
                >
                  {u.full_name}
                  <i className="bi bi-x"></i>
                </Badge>
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <div className="messages-user-list">
              {filteredUsers.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-people" style={{ fontSize: '1.75rem' }}></i>
                  <div className="mt-2 small">No se encontraron usuarios.</div>
                </div>
              ) : (
                filteredUsers.map((u) => {
                  const isSelected = selectedIds.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={`messages-user-row ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleUser(u.id)}
                    >
                      <TaskAssigneeAvatar assignee={u} size="md" />
                      <div className="messages-user-info">
                        <div className="messages-user-name">
                          {u.full_name || 'Usuario'}
                        </div>
                        <div className="messages-user-role">
                          {formatUserRole(u.role)}
                        </div>
                      </div>
                      {isSelected && (
                        <i className="bi bi-check-circle-fill messages-user-check"></i>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}

          {submitError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {submitError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={groupMutation.isLoading}
          >
            {groupMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creando...
              </>
            ) : (
              <>
                <i className="bi bi-people me-2"></i>
                Crear grupo
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

NewGroupModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConversationOpen: PropTypes.func,
};

export default NewGroupModal;
